import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, Input, model, OnDestroy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { TranslatePipe } from '@ngx-translate/core';
import { simpleDeepClone } from '@phx-erp/shared';
import { bounceAnimation, rubberBandAnimation } from 'angular-animations';
import { ButtonModule } from 'primeng/button';
import { DatePicker } from 'primeng/datepicker';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TabsModule } from 'primeng/tabs';
import { Textarea, TextareaModule } from 'primeng/textarea';
import { TimelineModule } from 'primeng/timeline';
import { TooltipModule } from 'primeng/tooltip';
import { lastValueFrom, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { LocaleDatePipe } from '../../../../../shared/pipes/locale-date.pipe';
import { SecondsToHoursPipe } from '../../../../../shared/pipes/seconds-to-hours.pipe';
import { S_CRM_LAST_START_OF_CALL } from '../../../../../@core/constants/settings-keys';
import { BaseListCombinedService } from '../../../../../@core/services/base-list-combined.service';
import { applySearchRule } from '../../../../../@core/utils/search-input-helper';
import { Address, AddressActivityCallInput, AddressActivityUnion, Operator, SearchInput, SearchRule, Task, TaskAttachedEntityType, TaskSearchInput } from '../../../../../generated/generated-types';
import { TaskService } from '../../../../task/services/task.service';
import { TasksWidgetComponent } from '../../../../task/components/tasks-widget/tasks-widget.component';
import { AddressService } from '../../../services/address.service';

interface AddressActivityWithFullAddress extends AddressActivityUnion {
  address: Address;
  delete?: () => void;
}

@Component({
  selector: 'app-widget-add-activity-control',
  templateUrl: './widget-add-activity-control.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    bounceAnimation(),
    rubberBandAnimation()
  ],
  standalone: true,
  imports: [
    CommonModule,
    TabsModule,
    DatePicker,
    ButtonModule,
    InputTextModule,
    Textarea,
    ProgressSpinnerModule,
    TextareaModule,
    TimelineModule,
    TooltipModule,
    TranslatePipe,
    SecondsToHoursPipe,
    LocaleDatePipe,
    FormsModule,
    TasksWidgetComponent,
  ]
})

export class WidgetAddActivityControlComponent implements OnDestroy {

  @Input()
  addressId: string;

  activeIndex: number = 0;

  si: SearchInput = {};
  loaded: boolean = false;
  isLoading = model<boolean>(true); //to not get flicker

  private destroy$ = new Subject();

  //#region call

  call: AddressActivityCallInput = {};

  // callModel: any = {};
  // callDate: Date = new Date();
  // startTime: Date = new Date();
  // endTime: Date = new Date();
  lastCallDateTime: Date;
  animateLastCall = false;
  addCallLoading = false;
  animateHistoryIcon = false;
  //#endregion

  activityHistories = model<AddressActivityWithFullAddress[]>([]);
  addressTasks: Task[] = [];
  readonly taskAttachedEntityType = TaskAttachedEntityType.ADDRESS;
  private baseListServices = inject(BaseListCombinedService);
  private addressService = inject(AddressService);
  private taskService = inject(TaskService);

  showNotes = false;
  selectedNotes = '';

  /**
   *
   */
  constructor() {
    let lc = this.baseListServices.settingsService.getSetting(S_CRM_LAST_START_OF_CALL);
    if (lc)
      this.lastCallDateTime = new Date(lc);
    this.baseListServices.settingsService.observeSetting(S_CRM_LAST_START_OF_CALL).pipe(takeUntil(this.destroy$)).subscribe(x => {
      this.lastCallDateTime = new Date(x);
      this.animateLastCall = true;
    });
  }

  ngOnChanges(c: any) {
    if (this.addressId) {
      this.buildFilter();
      this.loadHistory();
      void this.loadAddressTasks();
    } else {
      this.addressTasks = [];
    }
  }

  ngOnDestroy() {
    this.loaded = false;
    this.destroy$.next(0);
  }

  buildFilter() {
    let searchRule: SearchRule = {
      field: "addressId",
      operator: Operator.equal,
      value: this.addressId
    }
    applySearchRule(this.si, searchRule);
  }

  async addCallActivity() {

    this.call.addressId = this.addressId;

    try {
      this.addCallLoading = true;

      let res = await this.addressService.saveAddressActivityCall(this.call);
      this.call = {}; //reset
      this.animateHistoryIcon = true;
      this.addCallLoading = false;
      this.refreshHistory();
    } catch (e) {

    }

    this.baseListServices.notificationService.success(this.baseListServices.i18nService.translate(_("common.success")));
  }

  dateToday() {
    this.call.startDateTime = new Date();
  }

  startTimeNow() {
    this.call.startDateTime = new Date();
  }

  endTimeNow() {
    this.call.endDateTime = new Date();
  }

  useLastCallTimeAsStartTime() {
    if (this.lastCallDateTime != null)
      this.call.startDateTime = this.lastCallDateTime;
  }

  async loadHistory(force = false) {
    if (force || !this.loaded) {
      this.isLoading.set(true);

      let res = await this.addressService.getAddressActivities(this.si);
      res = simpleDeepClone(res);

      for (const activity of res.items as AddressActivityUnion[]) {
        let copy = simpleDeepClone(activity);
        if (!copy.durationSeconds) {
          if (copy.endDateTime && copy.startDateTime) {
            copy.durationSeconds = (new Date(copy.endDateTime).getTime() - new Date(copy.startDateTime).getTime()) / 1000;
          }
        }

        if (copy.durationSeconds < 1) {
          copy.durationSeconds = 0;
        }

        if (!copy.description) {
          copy.description = this.baseListServices.i18nService.translate(_("common.telephone-call"));
        }

        const fullAddress = await this.addressService.getItemData(activity.addressId);
        this.activityHistories.update((old) => [...old, {
          ...copy,
          address: fullAddress,
          delete: () => this.deleteActivityHistory({ ...copy, address: fullAddress })
        } as AddressActivityWithFullAddress]);
      }

      this.loaded = true;
      this.isLoading.set(false);
    }
  }

  deleteActivityHistory(activity: AddressActivityWithFullAddress) {
    if (activity && activity.id) {
      this.addressService.deleteAddressActivityCall(activity.id).then(() => {
        this.baseListServices.notificationService.success(_("common.success"));
        this.refreshHistory();  // Reload the history after deletion
      }).catch((error) => {
        console.error('Error deleting activity history:', error);
        this.baseListServices.notificationService.error(_("common.error"));
      });
    }
  }

  async refreshHistory() {
    this.activityHistories.set([]);
    this.loadHistory(true);
  }

  async loadAddressTasks() {
    if (!this.addressId) {
      this.addressTasks = [];
      return;
    }

    const result = await lastValueFrom(
      this.taskService.getItems({
        attachedEntityType: TaskAttachedEntityType.ADDRESS,
        attachedEntityId: this.addressId,
        take: 100,
      } as TaskSearchInput).mapSingle(x => x.getTasks),
    );

    this.addressTasks = result?.items ?? [];
  }

  onAddressTasksChange(tasks: Task[]) {
    this.addressTasks = tasks;
  }

  isTextTruncated(element: HTMLElement): boolean {
    return element.scrollHeight > element.clientHeight;
  }

  showNotesDialog(notes: string) {
    this.baseListServices.modalService.dialog({
      title: _('common.notes'),
      body: notes,
      size: 'sm',
      closable: true,
      buttons: []
    }).subscribe();
  }

}
